import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Typography,
  Box,
  Breadcrumbs,
  Link,
} from '@material-ui/core';
import FolderIcon from '@material-ui/icons/Folder';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import { Button } from '@clef/client-library';
import { Project, ProjectFolder } from '@clef/shared/types';
import { useGetAllProjectFoldersQuery } from '@/serverStore/projectFolders';
import {
  useMoveProjectFolderMutation,
  useMoveProjectToFolderMutation,
} from '@/serverStore/projectFolders';
import { useSnackbar } from 'notistack';
import { getDescendantFolderIds } from '@/utils/project_utils';
import CreateFolderDialog from './CreateFolderDialog';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    minWidth: 480,
    maxHeight: '70vh',
  },
  breadcrumbs: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  breadcrumbLink: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  list: {
    maxHeight: 300,
    overflow: 'auto',
  },
  listItem: {
    borderRadius: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  listItemSelected: {
    backgroundColor: theme.palette.action.selected,
  },
  folderIcon: {
    color: theme.palette.grey[500],
  },
  chevronIcon: {
    color: theme.palette.grey[400],
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  currentLocationText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

type MoveItem = { type: 'folder'; folder: ProjectFolder } | { type: 'project'; project: Project };

interface MoveToFolderDialogProps {
  open: boolean;
  onClose: () => void;
  item: MoveItem | null;
  isCollaborator?: boolean;
}

const MoveToFolderDialog: React.FC<MoveToFolderDialogProps> = ({
  open,
  onClose,
  item,
  isCollaborator = false,
}) => {
  const styles = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { data: allFolders = [] } = useGetAllProjectFoldersQuery();
  const moveFolder = useMoveProjectFolderMutation();
  const moveProject = useMoveProjectToFolderMutation();

  // Navigation state - which folder we're currently viewing
  const [currentViewFolderId, setCurrentViewFolderId] = useState<number | null>(null);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);

  const isMoving = moveFolder.isLoading || moveProject.isLoading;

  // Reset navigation when dialog opens
  useEffect(() => {
    if (open) {
      // Start at root level
      setCurrentViewFolderId(null);
    }
  }, [open]);

  // Get the current item's parent folder ID
  const itemCurrentFolderId = useMemo(() => {
    if (!item) return null;
    if (item.type === 'folder') {
      return item.folder.parentFolderId;
    }
    return item.project.projectFolderId ?? null;
  }, [item]);

  // Get folders that cannot be selected as destination (for folder moves)
  const excludedFolderIds = useMemo(() => {
    if (!item || item.type !== 'folder') return new Set<number>();
    const folderId = item.folder.id;
    const descendants = getDescendantFolderIds(folderId, allFolders);
    descendants.add(folderId); // Can't move into itself
    return descendants;
  }, [item, allFolders]);

  // Get folders at current navigation level (excluding the folder being moved and its descendants)
  const foldersAtCurrentLevel = useMemo(() => {
    return allFolders.filter(
      f => f.parentFolderId === currentViewFolderId && !excludedFolderIds.has(f.id),
    );
  }, [allFolders, currentViewFolderId, excludedFolderIds]);

  // Build breadcrumb path from root to current view folder
  const breadcrumbPath = useMemo(() => {
    const path: Array<{ id: number | null; name: string }> = [{ id: null, name: t('Projects') }];

    if (currentViewFolderId === null) return path;

    const buildPath = (folderId: number | null): void => {
      if (folderId === null) return;
      const folder = allFolders.find(f => f.id === folderId);
      if (folder) {
        buildPath(folder.parentFolderId);
        path.push({ id: folder.id, name: folder.name });
      }
    };

    buildPath(currentViewFolderId);
    return path;
  }, [currentViewFolderId, allFolders]);

  // Check if current view is a valid destination (not same as current location)
  const canMoveHere = useMemo(() => {
    return currentViewFolderId !== itemCurrentFolderId;
  }, [currentViewFolderId, itemCurrentFolderId]);

  const handleNavigateToFolder = useCallback((folderId: number) => {
    setCurrentViewFolderId(folderId);
  }, []);

  const handleBreadcrumbClick = useCallback((folderId: number | null) => {
    setCurrentViewFolderId(folderId);
  }, []);

  const handleMoveHere = useCallback(async () => {
    if (!item) return;

    try {
      if (item.type === 'folder') {
        await moveFolder.mutateAsync({
          id: item.folder.id,
          parentFolderId: currentViewFolderId,
        });
        enqueueSnackbar(t('Folder moved successfully'), { variant: 'success' });
      } else {
        await moveProject.mutateAsync({
          projectId: item.project.id,
          projectFolderId: currentViewFolderId,
        });
        enqueueSnackbar(t('Project moved successfully'), { variant: 'success' });
      }
      onClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: unknown }).message)
          : t('Failed to move item');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [item, currentViewFolderId, moveFolder, moveProject, enqueueSnackbar, onClose]);

  if (!item) return null;

  const itemName = item.type === 'folder' ? item.folder.name : item.project.name;
  const itemType = item.type === 'folder' ? t('folder') : t('project');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: styles.dialogPaper }}
      data-testid="move-to-folder-dialog"
    >
      <DialogTitle disableTypography>
        <Typography variant="h3">
          {t('Move {{itemType}} "{{itemName}}"', { itemType, itemName })}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* Breadcrumb navigation */}
        <Breadcrumbs separator="›" className={styles.breadcrumbs}>
          {breadcrumbPath.map((crumb, index) => {
            const isLast = index === breadcrumbPath.length - 1;
            return isLast ? (
              <Typography key={crumb.id ?? 'root'} color="textPrimary">
                {crumb.name}
              </Typography>
            ) : (
              <Link
                key={crumb.id ?? 'root'}
                className={styles.breadcrumbLink}
                color="inherit"
                onClick={() => handleBreadcrumbClick(crumb.id)}
              >
                {crumb.name}
              </Link>
            );
          })}
        </Breadcrumbs>

        {/* Folder list */}
        {foldersAtCurrentLevel.length > 0 ? (
          <List className={styles.list} dense>
            {foldersAtCurrentLevel.map(folder => (
              <ListItem
                key={folder.id}
                className={styles.listItem}
                button
                onClick={() => handleNavigateToFolder(folder.id)}
              >
                <ListItemIcon>
                  <FolderIcon className={styles.folderIcon} />
                </ListItemIcon>
                <ListItemText primary={folder.name} />
                <ChevronRightIcon className={styles.chevronIcon} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Box className={styles.emptyState}>
            <Typography variant="body2">{t('No subfolders')}</Typography>
          </Box>
        )}

        {/* Current location hint */}
        {!canMoveHere && (
          <Typography variant="body2" className={styles.currentLocationText}>
            {t('This is the current location')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {!isCollaborator && (
          <Button
            id="create-folder-in-move"
            onClick={() => setIsCreateFolderDialogOpen(true)}
            startIcon={<CreateNewFolderIcon />}
            style={{ marginRight: 'auto' }}
          >
            {t('Create Folder')}
          </Button>
        )}
        <Button id="cancel-move" onClick={onClose} disabled={isMoving}>
          {t('Cancel')}
        </Button>
        <Button
          id="confirm-move"
          onClick={handleMoveHere}
          variant="contained"
          color="primary"
          disabled={!canMoveHere || isMoving}
        >
          {isMoving
            ? t('Moving...')
            : currentViewFolderId === null
            ? t('Move to Root')
            : t('Move here')}
        </Button>
      </DialogActions>

      <CreateFolderDialog
        open={isCreateFolderDialogOpen}
        onClose={() => setIsCreateFolderDialogOpen(false)}
        parentFolderId={currentViewFolderId}
      />
    </Dialog>
  );
};

export default MoveToFolderDialog;
